/** Fallback when /public/business-types is unavailable (mirrors backend config/saas.php). */

/** Variants hidden on signup — enable extra modules later in Settings. */
export const SIGNUP_EXCLUDE_BUSINESS_TYPE_CODES = [
  'hardware_with_wakala',
  'bar_pub_with_wakala',
  'distribution',
];

export function filterSignupBusinessTypes(list) {
  const exclude = new Set(SIGNUP_EXCLUDE_BUSINESS_TYPE_CODES);
  const seen = new Set();
  return (list || []).filter((t) => {
    const code = t?.code;
    if (!code || exclude.has(code) || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
}

export const BUSINESS_TYPE_ICON_EMOJI = {
  printing: '🖨️',
  store: '🏪',
  pill: '💊',
  truck: '🚚',
  utensils: '🍽️',
  shirt: '💇',
  sofa: '🛋️',
  warehouse: '📦',
  hardware: '🔧',
  building2: '🏢',
  wakala: '📱',
  factory: '🏭',
  bar: '🍺',
  stethoscope: '🏥',
};

const MODULE_LABELS = {
  stock: 'Stock',
  pos: 'POS',
  orders: 'Orders',
  printing: 'Printing & copy',
  expenses: 'Expenses',
  cashbook: 'Wakala',
  reports: 'Reports',
  users: 'Team',
  debts: 'Customer debts',
  other_services: 'Other services',
  sms_monitor: 'SMS monitor',
  brick: 'Production (types, batches, deliveries)',
  subscription: 'Subscription & billing',
  security: 'Settings & security',
};

/** Matches backend saas.signup_hidden_modules — not shown on signup cards. */
const SIGNUP_HIDDEN = new Set(['security', 'subscription']);

const mod = (...keys) =>
  keys
    .filter((key) => !SIGNUP_HIDDEN.has(key))
    .map((key) => ({ key, label: MODULE_LABELS[key] || key }));

const retailShop = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'users');
const supermarketShop = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'users');
const restaurantFull = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'other_services', 'users');
const salonFull = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'users');
const clinicFull = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'users');
const retailWithServices = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'other_services', 'users');
const furnitureShowroom = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'other_services', 'users');
const printShop = mod(
  'stock',
  'pos',
  'orders',
  'printing',
  'expenses',
  'cashbook',
  'reports',
  'debts',
  'other_services',
  'users',
);
const serviceBiz = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'other_services', 'users');
const automotiveBiz = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'users');
const wakalaOnly = mod('cashbook', 'sms_monitor', 'reports', 'users');
const brickPlant = mod('brick', 'stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'users');
const barPubMvp = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'users');
const barPubWithWakala = mod(
  'stock',
  'pos',
  'orders',
  'expenses',
  'cashbook',
  'sms_monitor',
  'reports',
  'debts',
  'other_services',
  'users',
);
const hardwareStore = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'other_services', 'users');
const hardwareWithWakala = mod(
  'stock',
  'pos',
  'orders',
  'expenses',
  'reports',
  'debts',
  'other_services',
  'cashbook',
  'sms_monitor',
  'users',
);
const manufacturing = mod('stock', 'pos', 'orders', 'expenses', 'reports', 'debts', 'users');

const ALL_BUSINESS_TYPES = [
  {
    code: 'stationary',
    label: 'Stationery & print shop',
    icon: 'printing',
    description: 'Print & copy jobs, shop stock, supplier orders, wakala desk, and expenses.',
    modules: printShop,
  },
  {
    code: 'retail',
    label: 'Retail shop',
    icon: 'store',
    description: 'General retail: stock, POS, purchase orders, expenses, reports, and customer credit.',
    modules: retailShop,
  },
  {
    code: 'pharmacy',
    label: 'Pharmacy',
    icon: 'pill',
    description: 'Chemist: medicine categories, expiry & batch on stock, POS, supplier orders, patient credit, reports.',
    modules: retailShop,
  },
  {
    code: 'wholesale',
    label: 'Wholesale / distribution',
    icon: 'truck',
    description: 'Bulk B2B: stock, purchase orders, POS or dispatch sales, debts, and branch reports.',
    modules: retailShop,
  },
  {
    code: 'supermarket',
    label: 'Supermarket / mini-mart',
    icon: 'store',
    description: 'Fast POS, stock, supplier orders, expenses, reports, and customer accounts (credit).',
    modules: supermarketShop,
  },
  {
    code: 'restaurant',
    label: 'Restaurant / cafe',
    icon: 'utensils',
    description: 'Kitchen stock, POS, supplier orders, table/corporate credit, catering, and expenses.',
    modules: restaurantFull,
  },
  {
    code: 'salon_beauty',
    label: 'Salon & beauty',
    icon: 'shirt',
    description: 'Product stock, POS, supply orders, client credit balances, expenses, and reports.',
    modules: salonFull,
  },
  {
    code: 'electronics',
    label: 'Electronics shop',
    icon: 'store',
    description: 'Sales, stock, orders, credit sales, plus repair and installation (other services).',
    modules: retailWithServices,
  },
  {
    code: 'furniture',
    label: 'Furniture showroom',
    icon: 'sofa',
    description: 'Showroom POS, stock, orders, customer deposits/credit, delivery & assembly services.',
    modules: furnitureShowroom,
  },
  {
    code: 'clothing_boutique',
    label: 'Clothing / boutique',
    icon: 'shirt',
    description: 'Fashion retail: variants, POS, stock, purchase planning, and customer credit.',
    modules: retailShop,
  },
  {
    code: 'distribution',
    label: 'Distribution / logistics',
    icon: 'warehouse',
    description: 'Depot stock, purchase orders, dispatch sales, expenses, and multi-branch reports.',
    modules: retailShop,
  },
  {
    code: 'manufacturing',
    label: 'Light manufacturing',
    icon: 'hardware',
    description: 'Materials, finished goods stock, sales, purchase orders, expenses, and reports.',
    modules: manufacturing,
  },
  {
    code: 'clinic_healthcare',
    label: 'Clinic / healthcare desk',
    icon: 'stethoscope',
    description: 'Consumables stock, counter POS, supplier orders, patient bills on credit, and reports.',
    modules: clinicFull,
  },
  {
    code: 'service_business',
    label: 'Service business',
    icon: 'building2',
    description: 'Bill services, spare-parts stock, POS, orders, expenses, reports, and customer debts.',
    modules: serviceBiz,
  },
  {
    code: 'automotive_products',
    label: 'Automotive Products',
    icon: 'truck',
    description: 'Auto spare parts and accessories: stock, POS, supplier orders, expenses, reports, customer debts, and subscription billing.',
    modules: automotiveBiz,
  },
  {
    code: 'hardware',
    label: 'Hardware store',
    icon: 'hardware',
    description: 'Building materials: stock (with units), POS, customer quotations, supplier orders, contractor credit, cutting/delivery.',
    modules: hardwareStore,
  },
  {
    code: 'hardware_with_wakala',
    label: 'Hardware + M-Pesa desk',
    icon: 'hardware',
    description: 'Hardware shop plus wakala cashbook and SMS monitor on the same counter.',
    modules: hardwareWithWakala,
  },
  {
    code: 'wakala_agent',
    label: 'Mobile money (Wakala)',
    icon: 'wakala',
    description: 'Wakala cashbook and SMS monitor only — no stock, POS, or shop modules.',
    modules: wakalaOnly,
  },
  {
    code: 'brick_factory',
    label: 'Brick / tile factory',
    icon: 'factory',
    description: 'Production batches, materials, finished stock, deliveries, POS, orders, and factory reports.',
    modules: brickPlant,
  },
  {
    code: 'bar_pub',
    label: 'Bar / pub',
    icon: 'bar',
    description: 'MVP for TZ pubs: drinks stock, bar POS, supplier orders, expenses, reports, and customer tabs (deni).',
    modules: barPubMvp,
  },
  {
    code: 'bar_pub_with_wakala',
    label: 'Bar / pub + M-Pesa desk',
    icon: 'bar',
    description: 'Bar MVP plus wakala cashbook, SMS monitor, and cover/DJ/other bar services.',
    modules: barPubWithWakala,
  },
];

export const FALLBACK_BUSINESS_TYPES = filterSignupBusinessTypes(ALL_BUSINESS_TYPES);
